import React, { useEffect, useRef, useState } from 'react';

// Utilities
import API, { socketPurchases } from '../../../../utils/api';
import gaUtils from '../../../GAWrapper/GAWrapper';
import { waitUserCancelCashoutsPending } from '../../shared/utils/cashouts';
import { resetInput } from './utils';

// Store
import usePaymentStore from '../../shared/store/use-payment-store';

// Hooks
import { useOrkestaPay } from '../hooks/use-orkesta';

// Components
import styles from './card.module.css';

// Modals
import { showModalError } from '../../shared/components/modals/modal-error';
import {
  showModalIframe3DSecure,
} from '../../shared/components/modals/modal-iframe-3d-secure';
import { showModalSuccessPayment } from '../../shared/components/modals/modal-success';
import { waitVerificationName } from '../../shared/components/modals/modal-verification-name';

// Assets
import logo from '../assets/logo-orkestapay.png';
import { PAYMENT_METHOD } from '../../shared/constants';
import { CardFormFlat } from '../../shared/components/card-form/card-form-flat';

const parseError2String = (error) => error?.message ?? '';

export const OrkestaPayCards = ({ setSelectedMethod, ...props }) => {
  const { isInvalidAmount, amount } = usePaymentStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { deviceSessionId, orkestaPay } = useOrkestaPay();
  const orkestapayCardRef = useRef(null);
  const subscriptionsCleanupRef = useRef(null);

  const [fieldErrors, setFieldErrors] = useState({
    cardNumber: '',
    expiration: '',
    csc: '',
    holderName: '',
    holderLastName: '',
  });

  useEffect(() => {
    if (!orkestaPay) return;

    let cancelled = false;

    const timer = setTimeout(async () => {
      const cardNumberEl = document.getElementById('card-number');
      const expirationEl = document.getElementById('card-expiration');
      const cscEl = document.getElementById('card-csc');
      const holderNameEl = document.getElementById('card-holder-name');
      const holderLastNameEl = document.getElementById('card-holder-last-name');

      if (!cardNumberEl || !expirationEl || !cscEl || !holderNameEl || !holderLastNameEl) return;

      const promotions_params = { currency: 'MXN', total_amount: String(amount ?? '0') };

      let orkestapay_card;
      try {
        if (!orkestapayCardRef.current) {
          orkestapay_card = await orkestaPay.createCard({
            card_number: cardNumberEl,
            expiration_date: expirationEl,
            verification_code: cscEl,
            holder_name: holderNameEl,
            holder_last_name: holderLastNameEl,
            promotions_params,
          });
        }
      } catch (err) {
        console.error('Orkesta createCard error:', err);
        return;
      }

      if (cancelled || !orkestapay_card || typeof orkestapay_card !== 'object') return;

      orkestapayCardRef.current = orkestapay_card;

      // handleErrorEventChanges: suscribirse a errors$ de cada campo (como en example-orkesta.html)
      // La API puede devolver snake_case (card_number) o camelCase (cardNumber)
      const getField = (obj, snakeKey, camelKey) => obj?.[snakeKey] ?? obj?.[camelKey];
      const subs = [];

      const cardNumberField = getField(orkestapay_card, 'card_number', 'cardNumber');

      if (cardNumberField?.errors$) {
        subs.push(cardNumberField.errors$.subscribe((err) => {
          setFieldErrors((prev) => ({ ...prev, cardNumber: parseError2String(err) }));
        }));
      }

      const expirationField = getField(orkestapay_card, 'expiration_date', 'expirationDate');

      if (expirationField?.errors$) {
        subs.push(expirationField.errors$.subscribe((err) => {
          setFieldErrors((prev) => ({ ...prev, expiration: parseError2String(err) }));
        }));
      }

      const cscField = getField(orkestapay_card, 'verification_code', 'verificationCode');
      if (cscField?.errors$) {
        subs.push(cscField.errors$.subscribe((err) => {
          setFieldErrors((prev) => ({ ...prev, csc: parseError2String(err) }));
        }));
      }

      const holderNameField = getField(orkestapay_card, 'holder_name', 'holderName');
      if (holderNameField?.errors$) {
        subs.push(holderNameField.errors$.subscribe((err) => {
          setFieldErrors((prev) => ({ ...prev, holderName: parseError2String(err) }));
        }));
      }

      const holderLastNameField = getField(orkestapay_card, 'holder_last_name', 'holderLastName');
      if (holderLastNameField?.errors$) {
        subs.push(holderLastNameField.errors$.subscribe((err) => {
          setFieldErrors((prev) => ({ ...prev, holderLastName: parseError2String(err) }));
        }));
      }

      subscriptionsCleanupRef.current = () => {
        subs.forEach((sub) => sub?.unsubscribe?.());
        orkestapayCardRef.current = null;
      };
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      subscriptionsCleanupRef.current?.();
      subscriptionsCleanupRef.current = null;
    };
  }, [orkestaPay]);

  useEffect(() => {
    socketPurchases.on('purchase:completed', data => {
      const transaction = {
        amount: data.amount,
        provider: 'orkesta',
        transactionId: data?.trans_id,
      };

      API.trackPurchaseSuccessFB(transaction);
      gaUtils.trackUserPurchase(transaction);

      showModalSuccessPayment({
        amount: data.amount,
        balance: data.balance,
        trans_id: data.trans_id,
      });

      clearForm()

      if (props.dispatch) {
        props.updateUserInfo(props.dispatch);
      } else if (props.updateUserBalance) {
        props.updateUserBalance(data.balance);
      }
    });

    socketPurchases.on('purchase:failed', data => {
      showModalIframe3DSecure({
        url: data.status_url,
        title: 'Error',
      });
    });

    return () => {
      socketPurchases.off('purchase:completed');
      socketPurchases.off('purchase:failed');
    };
  }, []);

  const processPaymentWithNewCard = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (isSubmitting || isInvalidAmount) return;


    try {
      const isVerified = await waitVerificationName({
        title: 'Verificación de nombre',
      });

      if (!isVerified) {
        alert('Por favor, verifica tu cuenta para continuar.');
        return;
      }

      const userCancell = await waitUserCancelCashoutsPending();

      if (userCancell) {
        setIsSubmitting(false);
        return;
      }

      const orkestapay_card = orkestapayCardRef.current;

      const nombre = orkestapay_card?.holder_name?.getHolderName() ?? '';
      const apellido = orkestapay_card?.holder_last_name?.getHolderLastName() ?? '';
      const name = `${nombre} ${apellido}`.trim();

      const nameCheckResponse = await API.checkName(name);

      if (!nameCheckResponse.data.success) {
        showModalError({ errorMessage: nameCheckResponse.data?.message ?? 'Ha ocurrido un error' });
        return;
      }

      if (!orkestapay_card) {
        showModalError({ errorMessage: 'Formulario de tarjeta no listo. Intenta de nuevo.' });
        return;
      }

      const tokenizeCardResponse = await orkestapay_card.createToken({ one_time_use: true });

      const paymentDataModel = {
        amount,
        payment_method_id: tokenizeCardResponse.payment_method_id,
        device_session_id: deviceSessionId,
      }

      const response = await API.orkestaProcessCardPayment(paymentDataModel);

      if (!response.data.success) {
        showModalError({ errorMessage: response.data?.error ?? 'Ha ocurrido un error' });
        return;
      }

      if (response.data.redirect) {
        showModalIframe3DSecure({
          url: response.data?.action?.redirect_url,
          title: '3D Secure',
        });
      }
    } catch (error) {
      const errorMessage = error?.message ?? 'Ha ocurrido un error';
      showModalError({ errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearForm = async () => {
    resetInput('card-number');
    resetInput('card-expiration');
    resetInput('card-csc');
    resetInput('card-holder-name');
    resetInput('card-holder-last-name');
    setSelectedMethod(PAYMENT_METHOD.WELCOME)
  };

  return (
    <div className={styles.container}>
      <CardFormFlat
        onSubmit={processPaymentWithNewCard}
        isSubmitting={isSubmitting}
        disabled={isInvalidAmount}
        fieldErrors={fieldErrors}
      />


      <p className={styles.infoText}>
        Los cargos a tu tarjeta de crédito serán realizados a través de{' '}
        <a href="https://www.orkestapay.com/" target="_blank" rel="noopener noreferrer">
          OrkestaPay
        </a>
        .
      </p>

      <p className={styles.infoText}>
        <a href="https://www.orkestapay.com/" target="_blank" rel="noopener noreferrer">
          <img src={logo} alt="OrkestaPay" className={styles.orkestapayLogo} />
        </a>
      </p>
    </div>
  );
};
